/**
 * Client-side image compression, thumbnailing, and video poster extraction.
 *
 * Fixes over the legacy implementation:
 *  - ratio clamped to <= 1 (never upscale a small image)
 *  - rejects on image decode error (never hangs an upload forever)
 *  - always revokes object URLs (no leaks)
 *  - returns the chosen output MIME so the caller keeps key extension consistent
 */
import { UPLOAD_CONFIG } from '../config/awsConfig.ts';

export interface ProcessedImage {
  blob: Blob;
  /** e.g. 'image/jpeg' — caller should name the key accordingly. */
  type: string;
  width: number;
  height: number;
}

function loadImage(blob: Blob): Promise<{ img: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });
}

function fitWithin(width: number, height: number, maxEdge: number): { w: number; h: number } {
  const longest = Math.max(width, height);
  // Clamp to 1: only ever shrink, never enlarge.
  const ratio = Math.min(1, maxEdge / longest);
  return { w: Math.round(width * ratio), h: Math.round(height * ratio) };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
      type,
      quality,
    );
  });
}

async function rasterize(
  blob: Blob,
  maxEdge: number,
  type: string,
  quality: number,
): Promise<ProcessedImage> {
  // Prefer createImageBitmap with imageOrientation:'from-image' so EXIF
  // orientation is baked into the output (the legacy canvas path ignored it,
  // producing sideways thumbnails for rotated camera shots).
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      try {
        const { w, h } = fitWithin(bitmap.width, bitmap.height, maxEdge);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('2D canvas context unavailable');
        ctx.drawImage(bitmap, 0, 0, w, h);
        const out = await canvasToBlob(canvas, type, quality);
        return { blob: out, type, width: w, height: h };
      } finally {
        bitmap.close();
      }
    } catch {
      // Fall through to the <img> path below.
    }
  }

  // Fallback: <img> decode. Modern browsers apply EXIF orientation to <img>
  // by default, so this is also orientation-correct.
  const { img, url } = await loadImage(blob);
  try {
    const { w, h } = fitWithin(img.naturalWidth, img.naturalHeight, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    const out = await canvasToBlob(canvas, type, quality);
    return { blob: out, type, width: w, height: h };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Compress an image if it exceeds the threshold; otherwise return it untouched.
 * Compressed output is always JPEG.
 */
export async function compressImage(file: File): Promise<ProcessedImage> {
  if (file.size <= UPLOAD_CONFIG.COMPRESSION_THRESHOLD) {
    return { blob: file, type: file.type || 'application/octet-stream', width: 0, height: 0 };
  }
  return rasterize(
    file,
    UPLOAD_CONFIG.COMPRESSION_MAX_EDGE,
    'image/jpeg',
    UPLOAD_CONFIG.COMPRESSION_QUALITY,
  );
}

/** Generate a small JPEG thumbnail from an image file. */
export async function generateThumbnail(file: Blob): Promise<Blob> {
  const { blob } = await rasterize(
    file,
    UPLOAD_CONFIG.THUMBNAIL_MAX_EDGE,
    'image/jpeg',
    UPLOAD_CONFIG.THUMBNAIL_QUALITY,
  );
  return blob;
}

/**
 * Capture a poster frame from a video file as a JPEG thumbnail.
 * Resolves null if the browser can't decode the video (caller falls back to a placeholder).
 */
export function generateVideoPoster(file: File, seekTo = 1): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.src = url;

    const cleanup = () => URL.revokeObjectURL(url);

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(seekTo, (video.duration || seekTo) / 2);
    };
    video.onseeked = () => {
      try {
        const { w, h } = fitWithin(
          video.videoWidth,
          video.videoHeight,
          UPLOAD_CONFIG.THUMBNAIL_MAX_EDGE,
        );
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          resolve(null);
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            cleanup();
            resolve(blob);
          },
          'image/jpeg',
          UPLOAD_CONFIG.THUMBNAIL_QUALITY,
        );
      } catch {
        cleanup();
        resolve(null);
      }
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
  });
}
